// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type KpiBoxesProps } from '@shared/ui-kit/components';
import { numberToFixed } from '@shared/ui-kit/utils';
import { useMemo } from 'react';
import { type BacktestProps } from '../components/backtest';
import { useTranslation } from 'react-i18next';

export const useBacktest = ({ analysis, backtest }: BacktestProps) => {
  const { t } = useTranslation();
  const beta = analysis.risk.beta;

  const kpiData = useMemo<KpiBoxesProps[]>(
    () => [
      {
        value: `${numberToFixed(
          (backtest?.tracking?.tracking_difference as number) * 100,
        )}%`,
        text: t('backtest.tracking-difference', { ns: 'components' }),
        popupName: 'charts.trackingDifference',
        icon: 'Target',
        color: 'main',
      },
      {
        value: numberToFixed(beta as number),
        text: t('glossary.beta', { ns: 'glossary' }),
        popupName: 'kpis.beta',
        icon: 'Activity',
        color: 'secondary',
      },
      {
        value: numberToFixed(
          Math.min(...(backtest?.ts_drawdowns?.portfolio ?? [])) * 100,
        ),
        text: t('glossary.max-drawdown', { ns: 'glossary' }),
        popupName: 'kpis.maxDrawdown',
        icon: 'TrendingDown',
        color: 'danger',
      },
      {
        value: numberToFixed(backtest?.tracking?.r_square as number),
        text: t('glossary.r-square', { ns: 'glossary' }),
        popupName: 'kpis.rsquare',
        icon: 'ChartColumn',
        color: 'main',
      },
    ],
    [
      backtest?.tracking?.r_square,
      backtest?.tracking?.tracking_difference,
      backtest?.ts_drawdowns?.portfolio,
      beta,
      t,
    ],
  );

  return { kpiData };
};
